import React from "react";
import cn from "classnames";

import Icon from "../../../../icon";
import { ITranslationChatChannel } from "../../../../../common/types/translation/translationChatChannel";

import Loader, { LoaderWrapper } from "../../../../components/Loader";

import ChatChannel from "./ChatChannel";
import CreateNewChannel from "./CreateNewChannel";

import {
    Wrapper,
    Opener,
    ChatWrapper,
    Header,
    Title,
    Close,
    ChannelTitlesWrapper,
    ChannelTitle,
    Notification,
} from "./styles";
import { IProps, IState } from "./interfaces";

class TranslatorChat extends React.Component<IProps, IState> {
    state: IState = {
        isOpen: false,
        isBlur: false,
        channels: {
            value: [],
            isFetching: false,
        },
        activeChannel: null,
        lastestMessageDate: 0,
    };

    componentDidMount() {
        this.props.setEventSourceCallback(this.onEvent);
        this.getLastestMessageDate();
        window.addEventListener("blur", this.blur);
        window.addEventListener("focus", this.focus);
    }

    blur = () => {
        this.setState({ isBlur: true });
    };
    focus = () => {
        this.setState({ isBlur: false });
    };
    componentWillUnmount() {
        window.removeEventListener("blur", this.blur);
        window.removeEventListener("focus", this.focus);
    }
    getLastestMessageDate = () => {
        const { language } = this.props;
        this.props.get(`translation/chat/last-message-date/${language}`, res => {
            this.setState({
                lastestMessageDate: res.data,
            });
        });
    };

    setChatChecked = () => {
        const { language } = this.props;
        this.props.post(`translation/chat/set-chat-checked/${language}`, {}, res => {
            this.props.setChatChecked(res.data);
        });
    };

    onEvent = data => {
        if (this.state.isOpen && !this.state.isBlur) {
            this.setChatChecked();
        }
        if (data.event === "new-message") {
            const { message } = data;
            this.setState({
                lastestMessageDate: message.created,
            });
            this.setState(({ channels }) => ({
                channels: {
                    isFetching: false,
                    value: channels.value.map(channel => {
                        if (message.channel === channel._id) {
                            return {
                                ...channel,
                                messages: [...(channel.messages || []), message],
                            };
                        }
                        return channel;
                    }),
                },
            }));
        } else if (data.event === "new-channel") {
            this.setState(({ channels }) => ({
                channels: {
                    isFetching: false,
                    value: channels.value.concat(data.channel),
                },
            }));
        } else if (data.event === "delete-channel") {
            this.setState(
                ({ channels, activeChannel }) => ({
                    channels: {
                        isFetching: false,
                        value: channels.value.filter(channel => channel._id !== data.channelId),
                    },
                    activeChannel: activeChannel === data.channelId ? null : activeChannel,
                }),
                () => {
                    if (this.state.activeChannel === null) {
                        if (this.state.channels.value.length > 0) {
                            this.setState(({ channels }) => ({
                                activeChannel: channels.value[0]._id,
                            }));
                        } else {
                            this.setState(() => ({
                                activeChannel: "new-channel",
                            }));
                        }
                    }
                }
            );
        }
    };
    openChat = () => {
        this.setChatChecked();
        this.setState(
            {
                isOpen: true,
                channels: {
                    value: [],
                    isFetching: true,
                },
            },
            this.fetchChannels
        );
    };
    fetchChannels = () => {
        const { language } = this.props;
        this.props.get(`translation/chat/channels/${language}`, res => {
            this.setState({
                channels: {
                    value: res.data,
                    isFetching: false,
                },
                activeChannel: res.data.length > 0 ? res.data[0]._id : "new-channel",
            });
        });
    };
    closeChat = () => this.setState({ isOpen: false });
    createChannel = async (name: string) => {
        const { language } = this.props;
        return new Promise(resolve => {
            this.props.post(
                `translation/chat/create-channel/${language}`,
                { name },
                res => {
                    resolve(true);
                    this.setState(({ channels }) => ({
                        channels: {
                            isFetching: false,
                            value: channels.value.concat(res.data),
                        },
                        activeChannel: res.data._id,
                    }));
                },
                () => {
                    resolve(false);
                }
            );
        });
    };
    deleteChannel = async (deletingChannel: ITranslationChatChannel) => {
        const sure = window.confirm("You are about to delete this channel. Are you sure?");
        if (!sure) return;
        const { language } = this.props;
        this.props.post(`translation/chat/delete-channel/${language}`, { _id: deletingChannel._id }, () => {
            this.setState(
                ({ channels, activeChannel }) => ({
                    channels: {
                        isFetching: false,
                        value: channels.value.filter(channel => channel._id !== deletingChannel._id),
                    },
                    activeChannel: activeChannel === deletingChannel._id ? null : activeChannel,
                }),
                () => {
                    if (this.state.activeChannel === null) {
                        if (this.state.channels.value.length > 0) {
                            this.setState(({ channels }) => ({
                                activeChannel: channels.value[0]._id,
                            }));
                        } else {
                            this.setState(() => ({
                                activeChannel: "new-channel",
                            }));
                        }
                    }
                }
            );
        });
    };

    getMessages = async (updatingChannel: ITranslationChatChannel) => {
        const { language } = this.props;
        this.props.get(`translation/chat/messages/${language}?channelId=${updatingChannel._id}`, res => {
            this.setState(({ channels }) => ({
                channels: {
                    isFetching: false,
                    value: channels.value.map(channel => {
                        if (updatingChannel._id === channel._id) {
                            return {
                                ...channel,
                                messages: res.data.messages,
                            };
                        }
                        return channel;
                    }),
                },
            }));
        });
    };

    sendMessage = (updatingChannel: ITranslationChatChannel, message: string) => {
        const { language } = this.props;
        return new Promise<{ success: boolean }>(resolve => {
            this.props.post(
                `translation/chat/send-message/${language}?channelId=${updatingChannel._id}`,
                { message },
                res => {
                    resolve({ success: true });
                    this.setState(({ channels }) => ({
                        channels: {
                            isFetching: false,
                            value: channels.value.map(channel => {
                                if (updatingChannel._id === channel._id) {
                                    return {
                                        ...channel,
                                        messages: [...channel.messages, res.data],
                                    };
                                }
                                return channel;
                            }),
                        },
                    }));
                    this.setChatChecked();
                },
                () => {
                    resolve({ success: false });
                }
            );
        });
    };

    navigateToChannel = activeChannel => this.setState({ activeChannel });

    render() {
        const { isOpen, channels, activeChannel, lastestMessageDate } = this.state;
        const { users, chatCheckedAt } = this.props;
        return (
            <Wrapper>
                {!isOpen && (
                    <Opener onClick={this.openChat}>
                        <Icon i="messages" />
                        {chatCheckedAt < lastestMessageDate && <Notification />}
                    </Opener>
                )}
                {isOpen && (
                    <ChatWrapper>
                        <Header>
                            <Title>Chat</Title>
                            <Close onClick={this.closeChat}>×</Close>
                        </Header>
                        {channels.isFetching && (
                            <LoaderWrapper>
                                <Loader />
                            </LoaderWrapper>
                        )}
                        {!channels.isFetching && (
                            <>
                                <ChannelTitlesWrapper>
                                    {[
                                        ...channels.value.map(channel => (
                                            <ChannelTitle
                                                className={cn({ Active: activeChannel === channel._id })}
                                                key={channel._id}
                                                onClick={() => this.navigateToChannel(channel._id)}
                                            >
                                                {channel.name}
                                            </ChannelTitle>
                                        )),
                                        <ChannelTitle
                                            className={cn({ Active: activeChannel === "new-channel" })}
                                            key="new-channel"
                                            onClick={() => this.navigateToChannel("new-channel")}
                                        >
                                            New channel
                                        </ChannelTitle>,
                                    ]}
                                </ChannelTitlesWrapper>
                                {activeChannel === "new-channel" && (
                                    <CreateNewChannel handleCreateChannel={this.createChannel} />
                                )}
                                {activeChannel !== "new-channel" && activeChannel && (
                                    <ChatChannel
                                        key={activeChannel}
                                        getMessages={this.getMessages}
                                        channel={channels.value.find(channel => channel._id === activeChannel)}
                                        deleteChannel={this.deleteChannel}
                                        sendMessage={this.sendMessage}
                                        users={users}
                                    />
                                )}
                            </>
                        )}
                    </ChatWrapper>
                )}
            </Wrapper>
        );
    }
}

export default TranslatorChat;
